import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import ScheduleModal from "@/components/global/Chat/components/ScheduleModal/ScheduleModal";
import RemoveScheduleConfirmationModal from "@/components/global/Chat/components/ScheduleModal/RemoveScheduleConfirmationModal";
import { useReportingScheduler } from "@/entities/reportingScheduler/hooks";
import {
  useDeleteReport,
  useUnscheduleReport
} from "@/entities/savedReports/hooks";
import { SavedReport } from "@/entities/savedReports/types";
import { useHeldWhileClosing } from "@/hooks/useHeldWhileClosing";
import { useChat } from "@/components/layouts/Chat/ChatContext";
import { useReportRuns } from "@/components/global/Chat/components/dreamerReports/ReportRunsContext";
import { RemoveSavedReportModal } from "@/components/global/Chat/components/dreamerReports/RemoveSavedReportModal";
import { TRANSLATION_KEY } from "@/components/global/Chat/components/dreamerReports/consts";

interface ReportRowModalsProps {
  /** The report being scheduled or edited, or null when nothing is open. */
  scheduleTarget: SavedReport | null;
  /** The report whose schedule is being removed, or null when nothing is open. */
  removeScheduleTarget: SavedReport | null;
  /** The report being deleted, or null when nothing is open. */
  deleteTarget: SavedReport | null;
  onCloseSchedule: () => void;
  onCloseRemoveSchedule: () => void;
  onCloseDelete: () => void;
}

/**
 * The modals behind a report row's actions, mounted once per surface rather
 * than once per row.
 *
 * A row's lifetime is tied to the list it came from, and each of these modals
 * changes that list: removing a schedule drops the row out of the scheduled
 * filter, and deleting drops it outright. Owned by the row, a modal would be
 * unmounted by its own mutation mid-close, and a dialog torn down while open
 * never runs the cleanup that returns pointer events to the page.
 */
export const ReportRowModals: React.FC<ReportRowModalsProps> = ({
  scheduleTarget,
  removeScheduleTarget,
  deleteTarget,
  onCloseSchedule,
  onCloseRemoveSchedule,
  onCloseDelete
}) => {
  const { t } = useTranslation();
  const unscheduleReport = useUnscheduleReport();
  const deleteReportRow = useDeleteReport();
  // The chat answers naming a report read their saved state from the chat's
  // copy of the message, so a change made here is recorded there too.
  const { forgetSavedReport, setSavedReportScheduled } = useChat();
  const { forgetReportRun } = useReportRuns();

  // Each modal closes before it unmounts, and its content must not blank out
  // while the closing animation runs, so the last target is kept around.
  const [scheduleReport] = useHeldWhileClosing(scheduleTarget);
  const [removeScheduleReport] = useHeldWhileClosing(removeScheduleTarget);
  const [deleteReport] = useHeldWhileClosing(deleteTarget);

  // The modal edits delivery channels, which the list row does not carry, so it
  // needs the real schedule rather than a row-shaped stand-in — assembling one
  // from the row would silently reset the user's channels on save.
  const {
    data: existingSchedule,
    error: scheduleError,
    isSuccess: hasScheduleLoaded
  } = useReportingScheduler(
    scheduleReport?.isScheduled ? scheduleReport.id : null
  );

  // The modal decides between creating and updating on whether it was handed a
  // schedule, so opening it while the schedule is still in flight would put a
  // scheduled report into create mode and a save would add a second schedule.
  const isScheduleReady = !scheduleReport?.isScheduled || !!existingSchedule;

  // The gate above holds the modal shut until the schedule arrives, so a fetch
  // that fails — or a schedule already deleted elsewhere, which the hook maps
  // to null — would leave Edit schedule doing nothing at all. Falling through
  // to create mode instead would let a save add a second schedule, so say what
  // happened and hand the row back.
  const hasScheduleFailed =
    !!scheduleReport?.isScheduled &&
    (!!scheduleError || (hasScheduleLoaded && !existingSchedule));

  useEffect(() => {
    if (!scheduleTarget || !hasScheduleFailed) return;
    toast.error(t(`${TRANSLATION_KEY}.schedule.loadFailed`));
    onCloseSchedule();
  }, [scheduleTarget, hasScheduleFailed, onCloseSchedule, t]);

  const handleRemoveSchedule = () => {
    const reportId = removeScheduleReport?.id;
    if (!reportId) return;
    // Closed here rather than left to the modal, so both confirm handlers in
    // this file follow one contract: whichever modal sits behind them, the
    // surface that opened it is the one that shuts it.
    onCloseRemoveSchedule();
    // The cadence goes, the saved report stays: a report and its schedule are
    // one row, so deleting that row here would take the report with it.
    unscheduleReport.mutate(
      { pathParams: { id: reportId }, data: { scheduleConfig: null } },
      {
        onSuccess: () => {
          setSavedReportScheduled(reportId, false);
          toast.success(t(`${TRANSLATION_KEY}.removeSchedule.done`));
        },
        onError: () =>
          toast.error(t(`${TRANSLATION_KEY}.removeSchedule.failed`))
      }
    );
  };

  const handleDelete = () => {
    const reportId = deleteReport?.id;
    if (!reportId) return;
    // The confirmation modal leaves closing to its caller, and the row is gone
    // from the list either way: the toast reports how the delete went.
    onCloseDelete();
    deleteReportRow.mutate(
      { pathParams: { id: reportId } },
      {
        onSuccess: () => {
          forgetSavedReport(reportId);
          forgetReportRun(reportId);
          toast.success(t(`${TRANSLATION_KEY}.delete.done`));
        },
        onError: () => toast.error(t(`${TRANSLATION_KEY}.delete.failed`))
      }
    );
  };

  return (
    <>
      <ScheduleModal
        isOpen={!!scheduleTarget && isScheduleReady && !hasScheduleFailed}
        setIsOpen={(open) => {
          const nextValue =
            typeof open === "function" ? open(!!scheduleTarget) : open;
          if (!nextValue) onCloseSchedule();
        }}
        // A schedule hangs off the message the report was saved from, which
        // is what the create path keys on.
        messageId={scheduleReport?.originMessageId ?? ""}
        reportName={scheduleReport?.name ?? ""}
        // The row already exists, so its first schedule is a PUT against it. Without
        // this the modal falls through to the create path and posts a second save of
        // the same answer, which the server refuses.
        savedReportId={scheduleReport?.id ?? null}
        existingScheduleData={existingSchedule ?? null}
        onCreated={(created) => setSavedReportScheduled(created.id, true)}
        onCanceled={() => {
          if (scheduleReport) setSavedReportScheduled(scheduleReport.id, false);
        }}
      />
      <RemoveScheduleConfirmationModal
        isOpen={!!removeScheduleTarget}
        setIsOpen={(open) => {
          const nextValue =
            typeof open === "function" ? open(!!removeScheduleTarget) : open;
          if (!nextValue) onCloseRemoveSchedule();
        }}
        onConfirm={handleRemoveSchedule}
      />
      <RemoveSavedReportModal
        isOpen={!!deleteTarget}
        setIsOpen={(open) => {
          const nextValue =
            typeof open === "function" ? open(!!deleteTarget) : open;
          if (!nextValue) onCloseDelete();
        }}
        reportName={deleteReport?.name ?? ""}
        isScheduled={!!deleteReport?.isScheduled}
        onConfirm={handleDelete}
      />
    </>
  );
};
